import { useState, useEffect, useCallback } from 'react';
import {
  getSolutionsCatalog,
} from '../../../shell/events/solutionsCatalogBridge';
import type { CatalogItemSnapshot, SolutionsCatalogSnapshot } from '../../../shell/events/solutionsCatalogBridge';
import {
  buildSolutionCanvasMap,
  canvasRouteForModule,
} from '../../Esa/utils/solutionCanvasMap';
import type { WaffleItem } from '../data/types';

const BRIDGE_EVENT = '__SOLUTIONS_CATALOG_UPDATED__';
const DEFAULT_ICON = 'Layers';

function catalogItemToWaffleItem(
  item: CatalogItemSnapshot,
  canvasMap: Record<string, string>,
): WaffleItem | null {
  const agent = item.runtime_agent;
  if (!agent?.ukey) return null;
  if (agent.is_solution === false) return null;

  const canvasModule = canvasMap[agent.ukey];

  return {
    id: agent.ukey,
    name: agent.label ?? item.solution?.label ?? agent.ukey,
    type: 'solution',
    icon: DEFAULT_ICON,
    ...(canvasModule ? { route: canvasRouteForModule(canvasModule) } : {}),
  };
}

function buildPlatformItems(snapshot: SolutionsCatalogSnapshot): WaffleItem[] {
  const canvasMap = buildSolutionCanvasMap(snapshot);
  const items: WaffleItem[] = [];
  const seen = new Set<string>();

  for (const entry of snapshot.catalogItems) {
    const waffleItem = catalogItemToWaffleItem(entry, canvasMap);
    if (!waffleItem) continue;
    if (seen.has(waffleItem.id)) continue;
    seen.add(waffleItem.id);
    items.push(waffleItem);
  }

  return items;
}

/**
 * Reads the solutions catalog bridge and returns WaffleItems for the
 * "Platform Solutions" group. Falls back to null when the bridge is empty
 * (caller should keep using static data).
 *
 * Attaches canvas routes via the agent→module map so waffle selection navigates
 * (e.g. Ontology Builder → /operational/canvas/daxl-ontology-builder).
 */
export function useDynamicPlatformSolutions(): WaffleItem[] | null {
  const [items, setItems] = useState<WaffleItem[] | null>(() => {
    const snapshot = getSolutionsCatalog();
    if (!snapshot || snapshot.catalogItems.length === 0) return null;
    return buildPlatformItems(snapshot);
  });

  const readBridge = useCallback(() => {
    const snapshot = getSolutionsCatalog();
    if (!snapshot || snapshot.catalogItems.length === 0) return;
    setItems(buildPlatformItems(snapshot));
  }, []);

  useEffect(() => {
    readBridge();
    window.addEventListener(BRIDGE_EVENT, readBridge);
    return () => window.removeEventListener(BRIDGE_EVENT, readBridge);
  }, [readBridge]);

  return items;
}
